import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Grid } from '@mui/material';
import { scaleLinear, scaleBand, scaleOrdinal } from '@visx/scale';
import { BarStackHorizontal } from '@visx/shape';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { AxisBottom } from '@visx/axis';
import { max } from 'd3-array';
import PropTypes from 'prop-types';

import {
  getIndexFromObjList,
  thousandFormat,
} from '../../../../Utils/Functions';
import {
  daysRangeKeys,
  debtorColors,
  ship,
  thinAxis,
  barThinHeight,
} from '../../../../Assets/js/constant';

const xSelector = (d) => d.Total;
const ySelector = (d) => d.ProjectName;

const ProjectChart = ({
  detailData,
  selectedYears,
  selectedDaysRanges,
  selectedItems,
  selectedItemStacks,
  maxClientNameLength,
  maxProjectNameLength,
}) => {
  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip();

  const isMountedRef = useRef(false);
  const [data, setData] = useState([]);
  const [keys, setKeys] = useState([]);
  const [colors, setColors] = useState([]);
  const [selectedItemsState, setSelectedItemsState] = useState({ clients: [], projects: [], bars: [] });
  const [activeBar, setActiveBar] = useState(null);
  const [pageWidth, setPageWidth] = useState(window.innerWidth);
  const [resize, setResize] = useState(false);

  const onResize = useCallback(() => {
    if (isMountedRef.current) {
      setResize(prev => !prev);
      setPageWidth(window.innerWidth);
    }
  }, []);

  const deSelectAll = useCallback(() => {
    setSelectedItemsState({ clients: [], projects: [], bars: [] });
    localStorage.removeItem('dtSelectedItemStacks');
  }, []);

  const prepareData = useCallback(() => {
    const keys =
      selectedDaysRanges.length === 0 ||
      selectedDaysRanges[selectedDaysRanges.length - 1] === 'Total'
        ? daysRangeKeys
        : selectedDaysRanges;

    let colors = keys.reduce((ret, key) => {
      ret.push(debtorColors[key]);
      return ret;
    }, []);

    let data = {};
    detailData.forEach((d) => {
      if (
        (selectedItems.length === 0 && selectedItemStacks.length === 0) ||
        selectedItems.indexOf(d.Director) > -1
      ) {
        if (!data[d.ClientName]) data[d.ClientName] = {};
        if (!data[d.ClientName][d.ProjectName]) {
          data[d.ClientName][d.ProjectName] = {
            Total: 0,
            InvoiceNumber: d.InvoiceNumber,
            details: d,
          };
        }

        const curData = data[d.ClientName][d.ProjectName];

        daysRangeKeys.forEach((key) => {
          if (!curData[key]) curData[key] = 0;
          curData['Total'] += d[key] ? parseFloat(d[key]) : 0;
          curData[key] += parseFloat(d[key]);
        });
      } else {
        if (selectedItemStacks.length > 0) {
          daysRangeKeys.forEach((key) => {
            const stack = {
              director: d.Director,
              daysRange: key,
            };

            if (getIndexFromObjList(selectedItemStacks, stack) > -1) {
              if (!data[d.ClientName]) data[d.ClientName] = {};
              if (!data[d.ClientName][d.ProjectName]) {
                data[d.ClientName][d.ProjectName] = {
                  Total: 0,
                  InvoiceNumber: d.InvoiceNumber,
                  details: d,
                };
              }

              const curData = data[d.ClientName][d.ProjectName];
              if (!curData[key]) curData[key] = 0;
              curData['Total'] += parseFloat(d[key]);
              curData[key] += parseFloat(d[key]);
            }
          });
        }
      }
    });

    let resultData = Object.keys(data).map((ClientName) => {
      return Object.keys(data[ClientName]).map((ProjectName) => {
        let d = data[ClientName][ProjectName];
        d.ClientName = ClientName;
        d.ProjectName = ProjectName;

        keys.forEach((key) => {
          if (!d[key]) d[key] = 0;
        });

        return d;
      });
    });

    resultData.sort((a, b) =>
      a.reduce((a, b) => a + (b.Total || 0), 0) >
      b.reduce((a, b) => a + (b.Total || 0), 0)
        ? -1
        : 1,
    );

    setData(resultData);
    setKeys(keys);
    setColors(colors);
  }, [detailData, selectedDaysRanges, selectedItems, selectedItemStacks]);

  const handleElementSelect = useCallback((event, element, i, j, k) => {
    setSelectedItemsState(prevSelectedItems => {
      const selectedItems = { ...prevSelectedItems };
      
      if (j === undefined) {
        if (event.shiftKey) {
          if (element === 'Client') {
            const idx = selectedItems.clients.indexOf(i);
            if (idx >= 0) {
              selectedItems.clients.splice(idx, 1);
            } else {
              selectedItems.clients.push(i);
            }
            const prefix = `${i}-`;
            selectedItems.projects = selectedItems.projects.filter(
              (project) => !project.startsWith(prefix),
            );
            selectedItems.bars = selectedItems.bars.filter(
              (bar) => !bar.startsWith(prefix),
            );
          }
        } else {
          if (element === 'Client') {
            const idx = selectedItems.clients.indexOf(i);
            if (idx >= 0 && selectedItems.clients.length === 1)
              selectedItems.clients = [];
            else selectedItems.clients = [i];
            selectedItems.projects = [];
            selectedItems.bars = [];
          }
        }
      } else {
        if (event.shiftKey) {
          if (element === 'Client') {
            const value = `${i}-${j}`;
            const idx = selectedItems.projects.indexOf(value);
            if (idx >= 0) selectedItems.projects.splice(idx, 1);
            else selectedItems.projects.push(value);
            selectedItems.clients = selectedItems.clients.filter(
              (client) => client !== i,
            );
            const prefix = `${i}-${j}-`;
            selectedItems.bars = selectedItems.bars.filter(
              (bar) => !bar.startsWith(prefix),
            );
          }
        } else {
          if (element === 'Client') {
            const value = `${i}-${j}`;
            const idx = selectedItems.projects.indexOf(value);
            if (idx >= 0 && selectedItems.projects.length === 1)
              selectedItems.projects = [];
            else selectedItems.projects = [value];
            selectedItems.clients = [];
            selectedItems.bars = [];
          } else {
            const value = `${i}-${j}-${k}`;
            const idx = selectedItems.bars.indexOf(value);
            if (idx >= 0 && selectedItems.bars.length === 1)
              selectedItems.bars = [];
            else selectedItems.bars = [value];
            selectedItems.clients = [];
            selectedItems.projects = [];
          }
        }
      }

      return selectedItems;
    });
  }, []);

  const showTooltipHandler = useCallback((event, bar, position) => {
    let filterData = detailData.filter(
      (data) => data.ProjectId === bar.bar.data.details.ProjectId,
    );

    let invoiceDate = null;
    filterData.forEach((data) => {
      if (parseFloat(data[bar.key]) === bar.bar.data[bar.key])
        invoiceDate = data.InvoiceDate;
    });

    if (!invoiceDate) {
      for (var i = filterData.length - 1; i >= 0; i--) {
        if (parseFloat(filterData[i][bar.key]) !== 0) {
          invoiceDate = filterData[i].InvoiceDate;
          break;
        }
      }
    }

    if (activeBar) activeBar.classList.remove('barHover');
    event.target.classList.add('barHover');
    setActiveBar(event.target);

    const tooltipDataObj = {
      ...bar.bar.data.details,
      key: Math.ceil(
        (new Date().getTime() - new Date(invoiceDate).getTime()) /
          (1000 * 3600 * 24),
      ),
      index: bar.index,
      Amount: bar.bar.data[bar.key],
      Total: bar.bar.data.Total,
    };

    let top = event.nativeEvent.layerY;
    if (position < 6) {
      top += (6 - position) * 25;
    }

    showTooltip({
      tooltipData: tooltipDataObj,
      tooltipTop: top,
      tooltipLeft:
        window.innerWidth / 2 < event.nativeEvent.layerX
          ? event.nativeEvent.layerX - 350
          : event.nativeEvent.layerX + 50,
    });
  }, [detailData, activeBar, showTooltip]);

  const hideTooltipHandler = useCallback(() => {
    if (activeBar) activeBar.classList.remove('barHover');
    setActiveBar(null);
    hideTooltip();
  }, [activeBar, hideTooltip]);

  // Mount effect
  useEffect(() => {
    isMountedRef.current = true;
    prepareData();
    window.addEventListener('resize', onResize);

    return () => {
      isMountedRef.current = false;
      window.removeEventListener('resize', onResize);
    };
  }, [onResize]);

  // Update effect - using JSON.stringify for array comparison and specific length checks
  useEffect(() => {
    if (isMountedRef.current) {
      deSelectAll();
      prepareData();
    }
  }, [
    detailData.length, 
    selectedYears.length, 
    JSON.stringify(selectedDaysRanges),
    JSON.stringify(selectedItems),
    JSON.stringify(selectedItemStacks)
  ]);

  const selected = !(
    selectedItemsState.projects.length === 0 &&
    selectedItemsState.clients.length === 0 &&
    selectedItemsState.bars.length === 0
  );

  const height =
    pageWidth <= 1280 && pageWidth >= 1270
      ? 250
      : Math.floor((window.innerHeight / 3 + 5) / 25) * 25;
  const width =
    window.innerWidth -
    maxClientNameLength * 6.6 -
    maxProjectNameLength * 7 -
    60;

  // if (data.length === 0 || keys.length === 0) return null;

  let yScales = [];
  let xMaxValue = 0;

  data.forEach((d) => {
    yScales.push(
      scaleBand()
      .rangeRound([barThinHeight * d.length, 0])
      .domain(d.map(ySelector))
      .padding(0.2),
    );
    let m = max(d, xSelector);
    if (m > xMaxValue) xMaxValue = m;
  });

  const xScale = scaleLinear({
    range: [0, width],
    domain: [0, xMaxValue],
    nice: true,
  });

  const color = scaleOrdinal({
    domain: keys,
    range: colors,
  });

  let tooltipTimeout;

  return (
    <div style={{ paddingTop: 20 }}>
      <div className="subtitle mb-10">By Project</div>

      <div style={{ ...ship(height), paddingBottom: 3 }} id="debtors-bottom-chart">
        {data.length > 0 &&
          data.map((d1, i) => {
            const yMax = d1.length * barThinHeight;

            return (
              <Grid
                container
                key={i}
                sx={{
                  borderBottom: 'solid 1px #DDD',
                  '& p': {
                    height: barThinHeight + 'px',
                    margin: '0',
                    padding: '0 3px',
                    lineHeight: barThinHeight + 'px',
                  },
                }}
                style={{ height: 25 * d1.length }}
              >
                <div
                  className={
                    selectedItemsState.clients.includes(i)
                      ? 'grayHover bkgActive'
                      : 'grayHover'
                  }
                  style={{ width: maxClientNameLength * 6.6 }}
                  onClick={(event) =>
                    handleElementSelect(event, 'Client', i)
                  }
                >
                  <p
                    style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {d1[0].ClientName}
                  </p>
                </div>
                <div
                  style={{
                    borderRight: '1px solid darkgrey',
                    width: maxProjectNameLength * 7,
                  }}
                >
                  {d1.map((d2, j) => {
                    return (
                      <p
                        key={`${i}-${j}`}
                        className={
                          selectedItemsState.projects.includes(`${i}-${j}`)
                            ? 'grayHover bkgActive'
                            : 'grayHover'
                        }
                        style={{
                          paddingTop: 5,
                          lineHeight: 'unset',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        onClick={(event) =>
                          handleElementSelect(event, 'Client', i, j)
                        }
                      >
                        {d2.ProjectName}
                      </p>
                    );
                  })}
                </div>

                <div>
                  <svg width={width > 0 ? width : 0} height={yMax}>
                    <rect
                      x={0}
                      y={0}
                      width={width > 0 ? width : 0}
                      height={yMax}
                      fill="transparent"
                      onClick={deSelectAll}
                    />
                    <BarStackHorizontal
                      data={d1}
                      keys={keys}
                      height={yMax}
                      y={ySelector}
                      xScale={xScale}
                      yScale={yScales[i]}
                      color={color}
                    >
                      {(barStacks) => {
                        return barStacks.map((barStack, j) => {
                          const len = barStack.bars.length - 1;
                          return barStack.bars.map((bar, k) => {
                            const barValue = `${i}-${j}-${k}`;
                            const projValue = `${i}-${len - k}`;
                            const active =
                              !selected ||
                              selectedItemsState.clients.includes(i) ||
                              selectedItemsState.projects.includes(projValue) ||
                              selectedItemsState.bars.includes(barValue);

                            return (
                              <rect
                                key={`barstack-horizontal-${barStack.index}-${bar.index}`}
                                x={bar?.x}
                                y={4 + k * 25}
                                width={bar.width}
                                height={16.6}
                                fill={active ? bar.color : `#${bar.color}80`}
                                onClick={(event) => {
                                  handleElementSelect(
                                    event,
                                    'Bar',
                                    i,
                                    j,
                                    k,
                                  );
                                }}
                                onMouseMove={(event) => {
                                  if (tooltipTimeout)
                                    clearTimeout(tooltipTimeout);
                                  showTooltipHandler(event, bar, i);
                                }}
                                onMouseLeave={(event) => {
                                  tooltipTimeout = setTimeout(() => {
                                    hideTooltipHandler();
                                  }, 300);
                                }}
                                onTouchMove={(event) => {
                                  if (tooltipTimeout)
                                    clearTimeout(tooltipTimeout);
                                  showTooltipHandler(event, bar, i);
                                }}
                                onTouchEnd={(event) => {
                                  tooltipTimeout = setTimeout(() => {
                                    hideTooltipHandler();
                                  }, 300);
                                }}
                                className={
                                  selectedItemsState.bars.includes(barValue)
                                    ? 'barActive'
                                    : ''
                                }
                              />
                            );
                          });
                        });
                      }}
                    </BarStackHorizontal>
                  </svg>

                  {tooltipOpen && (
                    <TooltipWithBounds
                     
                      top={tooltipTop}
                      left={tooltipLeft}
                    >
                      <div className="bkg-white color-black" style={{ minWidth: 300, zIndex: 9999 }}>
                        <p className="tooltipPanel">
                          Client:&nbsp;
                          <strong>{tooltipData.ClientName}</strong>
                        </p>
                        <p className="tooltipPanel">
                          Project:&nbsp;
                          <strong>{tooltipData.ProjectName}</strong>
                        </p>
                        <p className="tooltipPanel">
                          Director:&nbsp;
                          <strong>{tooltipData.Director}</strong>
                        </p>
                        <br />
                        <p className="tooltipPanel">
                          Age:&nbsp;
                          <strong>{tooltipData.key} days</strong>
                        </p>
                        <p className="tooltipPanel">
                          Amount:&nbsp;
                          <strong>${thousandFormat(tooltipData.Amount)}</strong>
                        </p>
                        <p className="tooltipPanel">
                          Total:&nbsp;
                          <strong>${thousandFormat(tooltipData.Total)}</strong>
                        </p>
                      </div>
                    </TooltipWithBounds>
                  )}
                </div>
              </Grid>
            );
          })}
      </div>

      <div style={thinAxis} onClick={deSelectAll}>
        <svg
          width={window.innerWidth - 16 > 0 ? window.innerWidth - 16 : 0}
          height={barThinHeight}
          style={{ overflow: 'visible' }}
        >
          <rect
            x={0}
            y={0}
            width={window.innerWidth - 16 > 0 ? window.innerWidth - 16 : 0}
            height={barThinHeight}
            fill="transparent"
          />

          <AxisBottom
            scale={xScale}
            top={0}
            left={maxClientNameLength * 6.6 + maxProjectNameLength * 7 - 1}
            hideAxisLine={true}
            stroke="black"
            tickStroke="#a9a9a9"
            tickFormat={(value, index) => value / 1000}
            tickLabelProps={(value, index) => ({
              fill: 'black',
              fontSize: 11,
              textAnchor: 'middle',
              dy: '0.2em',
            })}
            tickComponent={({ formattedValue, ...tickProps }) => (
              <text {...tickProps}>
                $
                {Math.abs(formattedValue) < 1000
                  ? `${formattedValue}K`
                  : `${formattedValue / 1000}M`}
              </text>
            )}
          />
        </svg>
      </div>
    </div>
  );
};

ProjectChart.propTypes = {
  detailData: PropTypes.array.isRequired,
  selectedYears: PropTypes.array.isRequired,
  selectedDaysRanges: PropTypes.array.isRequired,
  selectedItems: PropTypes.array.isRequired,
  selectedItemStacks: PropTypes.array.isRequired,
  maxClientNameLength: PropTypes.number.isRequired,
  maxProjectNameLength: PropTypes.number.isRequired,
};

export default ProjectChart;
